/**
 * Goodbye Letter Settings Service — CRUD over
 * `investor_encompass_template_goodbye_settings`. Local-only data; nothing
 * here flows from Encompass.
 *
 * The caller never supplies `organization_id` — the service derives it from
 * the parent template row to keep the composite FK satisfied without leaking
 * org concerns into the UI layer.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type {
  GoodbyeLetterSettings,
  GoodbyeLetterSettingsInput,
  GoodbyeLetterSettingsRow
} from '@/types/goodbye-letter';
import {
  fetchGoodbyeLetterSettings,
  rowToGoodbyeLetterSettings as rowToSettings
} from '../../supabase/functions/_shared/goodbye-letter-settings';

const log = createComponentLogger('GoodbyeLetterSettingsService', 'database');

export async function getGoodbyeLetterSettings(
  templateId: string
): Promise<GoodbyeLetterSettings | null> {
  return fetchGoodbyeLetterSettings(supabase, templateId);
}

/**
 * Insert or update goodbye-letter settings for the given template. On insert
 * we look up the template to derive `organization_id`; on update we leave
 * org/template_id untouched.
 */
export async function upsertGoodbyeLetterSettings(
  templateId: string,
  input: GoodbyeLetterSettingsInput
): Promise<GoodbyeLetterSettings> {
  if (Object.keys(input).length === 0) {
    const existing = await getGoodbyeLetterSettings(templateId);
    if (existing) {
      return existing;
    }
  }

  const { data, error } = await supabase.rpc('upsert_investor_template_goodbye_settings', {
    p_template_id: templateId,
    p_patch: input
  });

  if (error) {
    log.error('Failed to upsert goodbye-letter settings', { templateId }, error);
    throw error;
  }
  return rowToSettings(data as GoodbyeLetterSettingsRow);
}
